import { IObj2d, subtract } from "../../models/IObj";
import { IInteriorItem, InteriorItem, InteriorProductType, Vec2d } from "../WebApiClient";
import { ICabinetWithDimensions as ICabinet, ICabinetWithDimensions } from "../../models/IcabinetWithDimensions";
import { CabinetService } from "./CabinetService";
import { PlaceItemInfo } from "./InteriorService";
import { IItemInfo } from "../../models/IItemInfo";
import { BaseState } from "./BaseState";
import { Section } from "./Section";
import { IInteriorProductSubGroup, IVec2d } from "../../models/IChainInfo";
import { GableType } from "../../models/GableType";


export interface DragInfo extends IObj2d, PlaceItemInfo {
    readonly textureUrl: string;
    readonly productId: number;
    readonly shadows: IObj2d[];
    readonly isFlipped?: boolean;
    parentIndexes: number[];
};


export interface DragServiceProps {
    readonly cabinet: ICabinetWithDimensions;
    readonly cabinetService: CabinetService;
    readonly productSubGroup: IInteriorProductSubGroup;
    readonly item: IInteriorItem | undefined;
    readonly cursorModelPos: IVec2d;
    readonly mouseOffset: IVec2d;
}


export interface IDragService<T extends DragInfo = DragInfo> {
    getDragInfo(cursorModelPos: IVec2d, forceCopy: boolean): T | undefined;
    place(dragInfo: T, forceCopy: boolean): IInteriorItem[];
}

interface PrevGableInfo {
    readonly maxPos: IVec2d;
    readonly item?: IItemInfo;
}

interface GableInfo {
    readonly pos: IVec2d;
    readonly maxPos: IVec2d;
    readonly size: IVec2d;
    readonly item?: IItemInfo;
}

export abstract class DragService<T extends DragInfo = DragInfo> implements IDragService<T> {
    protected static readonly moduleMaterialThickness = 16.5;
    protected readonly baseState: BaseState;
    protected readonly mouseOffset: IVec2d;
    protected readonly floorY = 0; //TODO get floor height
    protected readonly cabinet: ICabinet;
    protected readonly cabinetService: CabinetService;
    protected readonly productSubGroup: IInteriorProductSubGroup;
    protected readonly item: IInteriorItem | undefined;

    constructor(props: DragServiceProps) {
        this.cabinet = props.cabinet;
        this.cabinetService = props.cabinetService;
        this.productSubGroup = props.productSubGroup;
        this.item = props.item;
        this.baseState = this.getBaseState();
        this.mouseOffset = props.mouseOffset ?? ((!!props.item)
            ? subtract(props.cursorModelPos, props.item.position)
            : { x: 0, y: 0 });
        console.log({ psg: props.productSubGroup });
    }


    abstract getDragInfo(cursorModelPos: IVec2d, forceCopy: boolean): T | undefined;

    //This method is good for overriding
    place(dragInfo: T, forceCopy: boolean): IInteriorItem[] {
        const shouldCopy = forceCopy || (!this.item);
        return shouldCopy
            ? this.copy(dragInfo)
            : this.move(dragInfo)
            ;
    }


    //This method is good for overriding
    protected copy(dragObject: T): IInteriorItem[] {
        const newIndex = (this.cabinet.interiorItems.length > 0)
            ? Math.max(...this.cabinet.interiorItems.map(i => i.index)) + 1
            : 1;

        const newItem = new InteriorItem({
            position: new Vec2d(dragObject.position),
            size: new Vec2d(dragObject.size),
            productId: dragObject.productId,
            index: newIndex,
            isFlipped: dragObject.isFlipped ?? false,
            parentIndexes: dragObject.parentIndexes,
            interiorMaterialId: this.item?.interiorMaterialId ?? this.cabinet.interiorMaterialId
        });
        const newItems = this.cabinet.interiorItems.concat(newItem);
        return newItems;
    }

    //This method is good for overriding
    protected move(itemInfo: T): IInteriorItem[] {
        if (!this.item)
            throw new Error("DragService.item === null");

        const newItem = new InteriorItem({
            ...this.item,
            size: new Vec2d(itemInfo.size),
            position: new Vec2d(itemInfo.position),
            productId: itemInfo.productId,
            isFlipped: itemInfo.isFlipped ?? false,
            parentIndexes: itemInfo.parentIndexes,
        });

        const newItems = this.baseState.otherItems
            .map(itemInfo => itemInfo.item)
            .concat(newItem);
        return newItems;
    }

    protected getProduct(item: IInteriorItem): IItemInfo {
        const product = this.cabinetService.interior.getProduct(item.productId);
        return { product, item };
    }

    private getBaseState(): BaseState {
        const otherItems = this.cabinet.interiorItems
            .filter(item => item !== this.item)
            .map(item => this.getProduct(item))
            ;
        const otherGables = otherItems.filter(i => i.product.productSubGroup.productType === InteriorProductType.Gable);
        const otherModules = otherItems.filter(i => i.product.productSubGroup.productType === InteriorProductType.Module);
        return {
            otherItems,
            otherGables,
            otherModules,
            sections: this.getSections(otherGables, otherModules),
        };
    }

    private getSections(otherGables: IItemInfo[], otherModules: IItemInfo[]): Section[] {

        const inf = this.cabinetService.getGableInfo();
        let left = inf.left.type === GableType.Gable
            ? inf.left.position.x + inf.left.size.x
            : 0;

        let sections: Section[] = [];
        let prevGables: PrevGableInfo[] = [{ // This element represents the left outer gable or the house wall.
            maxPos: { x: left, y: this.cabinet.height },
            item: undefined
        }];
        const otherGableLikeStuff = otherGables.concat(otherModules);
        otherGableLikeStuff.sort((a, b) => a.item.position.x - b.item.position.x);

        for (let gable of otherGableLikeStuff) {
            let gableInfo: GableInfo = {
                pos: gable.item.position,
                maxPos: {
                    x: gable.item.position.x + gable.item.size.x,
                    y: gable.item.position.y + gable.item.size.y
                },
                size: gable.item.size,
                item: gable
            };
            sections.push(...this.getSectionsForGable(gableInfo, prevGables));

            prevGables.push({
                item: gable,
                maxPos: gableInfo.maxPos,
            });
        }

        const right = inf.right.type === GableType.Gable
            ? inf.right.position.x
            : this.cabinet.width!;

        let rightOuterGableInfo: GableInfo = {
            pos: {
                x: right,
                y: 0 //TODO find floor height
            },
            maxPos: {
                x: this.cabinet.width,//Not used
                y: this.cabinet.height,
            },
            size: {
                x: 0,
                y: this.cabinet.height,
            },
            item: undefined,
        };
        sections.push(...this.getSectionsForGable(rightOuterGableInfo, prevGables));

        sections.push(...this.getSectionsForModules(otherModules));

        sections.sort((secA, secB) => secA.position.x - secB.position.x);

        return sections;
    }

    private getSectionsForModules(otherModules: IItemInfo[]): Section[] {
        return otherModules.map(mod => ({
            leftGable: mod,
            rightGable: mod,
            position: {
                x: mod.item.position.x + DragService.moduleMaterialThickness,
                y: mod.item.position.y
            },
            size: {
                x: mod.item.size.x - (2 * DragService.moduleMaterialThickness),
                y: mod.item.size.y
            },
            isModule: true,
            stepHeight: 32,  //TODO get correct step offset
            stepOffset: 32,  //TODO get correct step height
        }));
    }

    private getSectionsForGable(gable: GableInfo, prevGables: PrevGableInfo[]): Section[] {
        const result: Section[] = [];

        let yMin = gable.pos.y;
        for (let i = prevGables.length - 1; i >= 0; i--) {
            const prevGable = prevGables[i];
            if (prevGable.maxPos.x >= gable.pos.x) {
                //gables are right next to each other, do not make a section

                if (prevGable.maxPos.y <= (gable.maxPos.y)) {
                    prevGables.pop();
                }
                continue; 
            }
            result.push({
                leftGable: prevGable.item,
                rightGable: gable.item,
                position: { x: prevGable.maxPos.x, y: yMin },
                size: {
                    x: gable.pos.x - prevGable.maxPos.x,
                    y: Math.min(gable.pos.y + gable.size.y, prevGable.maxPos.y) - yMin
                },
                stepOffset: 32, //TODO get correct step offset
                stepHeight: 32, //TODO get correct step height
                isModule: false, 
            });
            if (gable.maxPos.y >= prevGable.maxPos.y) {
                // current gable is taller than previous gable.
                // it is no longer possible to make any more sections reaching prevGable,
                // so remove it from lookback list
                yMin = prevGable.maxPos.y;
                prevGables.pop();

            }
            else {
                break;
            }
        }
        return result;
    }
}

